import React from 'react';
import { useTheme } from '@emotion/react';
import PropTypes, { InferProps } from 'prop-types';
import {
  LoginSignupLayout,
  Subtitle,
} from '@fuww/library/src/Login';
import Subtitle2 from '@fuww/library/src/Typography/SubTitle2';
import Hr from '@fuww/library/src/Elements/Hr';
import { useIntl } from 'react-intl';
import Head from '../Head';
import HomeLink from '../HomeLink';
import LinkAndAnchor from '../LinkAndAnchor';
import CookieConsentMessage from '../CookieConsent/Message';
import Disclaimer from './Disclaimer';
import messages from '../../lib/messages.json';
import RedirectWhenLoggedIn from './RedirectWhenLoggedIn';
import { retrieveDestination } from '../../lib/routerState';
import Routes from '../../routes.mjs';
import findRegistrar from '../../lib/findRegistrar';
import { Registrar } from '../../lib/graphql/api/graphql';

const SignupHead = () => {
  const intl = useIntl();
  return (
    <Head
      title={intl.formatMessage(messages['register.title'])}
      description={
        intl.formatMessage(messages['markets.description'])
      }
    />
  );
};

interface UserRegistrationInputs {
  firstName?: string;
  lastName?: string;
  email: string;
  password?: string;
  captchaResponse?: string | null;
  chamberOfCommerceNumber?: string;
  companyName?: string;
  country?: string;
  language?: string;
  websiteUrl?: string;
  registeredVia: Registrar;
}

export const handleUserRegistration = (
  registerUser: (
    options: { variables: Record<string, unknown> }
  ) => Promise<unknown>,
  userInputs: UserRegistrationInputs,
  locale: string,
) => {
  const { route, query } = retrieveDestination(
    'index',
    {},
    locale,
  );

  const {
    firstName,
    lastName,
    email,
    password,
    captchaResponse,
    chamberOfCommerceNumber,
    companyName,
    country,
    language,
    websiteUrl,
    registeredVia,
  } = userInputs;

  const redirectUrl = new URL(password ? Routes.findAndGetUrls(
    route,
    query,
  ).urls.as : Routes.findAndGetUrls(
    'set-password',
    {},
  ).urls.as, window.location.origin).toString();

  registerUser({
    variables: {
      firstName,
      lastName,
      name: firstName && lastName && `${firstName} ${lastName}`,
      email,
      password,
      chamberOfCommerceNumber,
      companyName,
      country,
      language,
      websiteUrl,
      captchaResponse,
      redirectUrl,
      registeredVia: findRegistrar(redirectUrl, registeredVia),
    },
  });
};

const signupPropertyTypes = {
  children: PropTypes.node.isRequired,
  infoPanel: PropTypes.node,
  redirectLink: PropTypes.node,
  showBreadcrumbs: PropTypes.bool,
};

type SignupProperties = InferProps<typeof signupPropertyTypes>;

const Signup = ({
  infoPanel,
  redirectLink,
  children,
  showBreadcrumbs,
}: SignupProperties) => {
  const intl = useIntl();
  const theme = useTheme();

  const breadcrumbs = (showBreadcrumbs ?? true) ? [
    <HomeLink />,
    <LinkAndAnchor
      route="register"
    >
      {intl.formatMessage(messages.signup)}
    </LinkAndAnchor>,
  ] : [];

  return (
    <LoginSignupLayout
      breadcrumbs={breadcrumbs}
      title={intl.formatMessage(messages['register.title'])}
      disclaimer={(<Disclaimer />)}
      infoPanel={infoPanel ?? null}
      redirectLink={redirectLink ?? null}
    >
      <SignupHead />
      <CookieConsentMessage />
      {children}
      <Subtitle
        prefix={intl.formatMessage(messages['register.alreadyRegistered'])}
        anchor={(
          <LinkAndAnchor
            route="login"
            styled
          >
            <Subtitle2 tag="span">
              {intl.formatMessage(messages.login)}
            </Subtitle2>
          </LinkAndAnchor>
        )}
      />
      <Hr backgroundColor={theme.colors.blackDisabled} margin="24px 0" />
      <RedirectWhenLoggedIn />
    </LoginSignupLayout>
  );
};

Signup.propTypes = signupPropertyTypes;

export default Signup;
